import { ClipboardPaste, FileUp } from "lucide-react";
import { format } from "date-fns";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReferenceScheduleStore } from "../../stores/reference-schedule-store";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Textarea } from "../ui/textarea";
import { ReferenceScheduleRow } from "./ReferenceScheduleRow";

export function ReferenceSchedules({ monday }: { monday: Date }) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [failed, setFailed] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const { references, nextNumber, add } = useReferenceScheduleStore();
  const mondayKey = format(monday, "yyyy-MM-dd");

  const tryAdd = (name: string, text: string) => {
    setFailed(false);
    try {
      add(name, text);
      return true;
    } catch (error) {
      console.error("Failed to parse reference schedule JSON", error);
      setFailed(true);
      return false;
    }
  };

  const loadFile = async (file: File) => {
    const name = file.name.replace(/\.json$/i, "");
    tryAdd(name, await file.text());
  };

  const loadPaste = () => {
    const name = t("reference.defaultName", { number: nextNumber });
    const loaded = tryAdd(name, pasteText);
    if (!loaded) return;
    setPasteText("");
    setPasteOpen(false);
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-neutral-600">{t("reference.title")}</span>
        <Button variant="outline" size="xs" onClick={() => input.current?.click()}><FileUp />{t("reference.loadFile")}</Button>
        <Popover open={pasteOpen} onOpenChange={setPasteOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="xs"><ClipboardPaste />{t("reference.paste")}</Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 space-y-2 bg-white">
            <Textarea value={pasteText} onChange={(event) => setPasteText(event.target.value)} placeholder={t("reference.pastePlaceholder")} className="h-32 font-mono text-xs" />
            <Button size="sm" disabled={!pasteText.trim()} onClick={loadPaste}>{t("reference.add")}</Button>
          </PopoverContent>
        </Popover>
        <input ref={input} type="file" accept=".json,application/json" className="hidden" onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) loadFile(file);
        }} />
      </div>
      {references.length > 0 && (
        <ul className="contents">
          {references.map((reference) => <ReferenceScheduleRow key={reference.id} reference={reference} monday={mondayKey} />)}
        </ul>
      )}
      {failed && <p role="alert" className="basis-full text-xs text-red-600">{t("reference.jsonError")}</p>}
    </>
  );
}
